import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import Footer from "../components/Footer";
import FloatingThemeToggle from "../components/FloatingThemeToggle";

const ImprintPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <FloatingThemeToggle />
      <div className="bg-gradient-to-br from-gray-900 light:from-gray-50 via-gray-800 light:via-gray-100 to-twitch-900 light:to-twitch-100 flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-white light:text-gray-900 mb-2">Imprint</h1>
            <p className="text-gray-400 light:text-gray-600">
              Legal information and contact details
            </p>
          </div>

          <div className="bg-gray-800/50 light:bg-white/50 rounded-lg p-8 space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                Service Provider
              </h2>
              <div className="text-gray-300 light:text-gray-700 space-y-2">
                <p>
                  <strong>Operated by:</strong> Jan Blömacher
                  <br />
                  <strong>Address:</strong> Waldstraße 3, 56412 Nomborn, Germany
                  <br />
                  <strong>Email:</strong> <a
                    href="mailto:hello@overcode.tv"
                    className="text-twitch-400 hover:text-twitch-300"
                  >
                    hello@overcode.tv
                  </a>
                  <br />
                </p>
                <p>
                  <strong>Service:</strong> Waddup.me - Twitch Chat Greeting
                  Application
                </p>
                <p>
                  <strong>Website:</strong>{" "}
                  <a
                    href="https://waddup.me"
                    className="text-twitch-400 hover:text-twitch-300"
                  >
                    waddup.me
                  </a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                Contact Information
              </h2>
              <div className="text-gray-300 light:text-gray-700 space-y-2">
                <p>
                  <strong>All Inquiries:</strong>{" "}
                  <a
                    href="mailto:hello@overcode.tv"
                    className="text-twitch-400 hover:text-twitch-300"
                  >
                    hello@overcode.tv
                  </a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                Technical Information
              </h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  Waddup.me is a web application that integrates with Twitch to
                  help streamers manage viewer greetings. The service uses
                  official Twitch APIs and follows Twitch's Terms of Service and
                  Developer Agreement.
                </p>
                <p>
                  This service is built with modern web technologies and hosted
                  on secure, regularly updated infrastructure to ensure
                  reliability and data protection.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                Liability Disclaimer
              </h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  The information on this website is provided on an "as is"
                  basis. To the fullest extent permitted by law, I exclude all
                  representations, warranties, and conditions relating to this
                  website and the use of this website.
                </p>
                <p>I will not be liable for any loss or damage arising from:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Use of or reliance on information on this website</li>
                  <li>Interruption or unavailability of the service</li>
                  <li>Any third-party services including Twitch</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                Third-Party Services
              </h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  Waddup.me integrates with and depends on third-party services:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    <strong>Twitch:</strong> For authentication and chat data
                    access. Subject to{" "}
                    <a
                      href="https://www.twitch.tv/p/legal/terms-of-service/"
                      className="text-twitch-400 hover:text-twitch-300"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Twitch Terms of Service
                    </a>
                  </li>
                  <li>
                    <strong>Hosting Provider:</strong> For secure data storage
                    and application hosting
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                Data Protection
              </h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  I take data protection seriously and comply with applicable
                  data protection laws. For detailed information about how your
                  data is handled, please see the{" "}
                  <Link
                    to="/privacy"
                    className="text-twitch-400 hover:text-twitch-300"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                Dispute Resolution
              </h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  Any disputes arising from the use of this service should first
                  be addressed through direct contact via email. I am committed
                  to resolving any issues promptly and fairly.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                Last Updated
              </h2>
              <div className="text-gray-300 light:text-gray-700">
                <p>
                  This imprint was last updated on{" "}
                  {new Date().toLocaleDateString()}.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ImprintPage;
